import { Component, Prop, Vue } from 'vue-property-decorator'
import { debounce } from '@/lib/lodash'
import CKEditor from 'ckeditor4-vue'
import getCountLetters from '@/helpers/getCountLetters'
import decodeHTMLString from '@/helpers/decodeHTMLString'
import sliceText from '@/helpers/sliceText'
import SpellCheck from './plugins/spellCheck'
import htmlToText from '@/helpers/htmlToText'

const cleanEditorText = (value: string) => value.replace(/\n+/g, ' ').trim()

@Component({
  components: {
    ckeditor: CKEditor.component,
  },
  inheritAttrs: false,
})
export default class Editor extends Vue {
  @Prop({ type: String })
  private readonly toolbarClassName?: string
  @Prop({ type: String })
  private readonly placeholder?: string
  @Prop({ type: Boolean, default: true })
  private readonly hiddenCounter!: boolean
  @Prop({ type: Boolean, default: false })
  private readonly disabled!: boolean
  @Prop({ type: String, default: '' }) private readonly value!: string
  @Prop({ type: Number, default: 200 }) private readonly throttle!: number
  @Prop({ type: String, default: 'html-text' })
  private readonly valueType!: 'plain-text' | 'html-text'
  @Prop({ type: Number }) private readonly maxlength?: number
  @Prop({ type: Object }) private readonly config?: CKEDITOR.config

  private isFocused = false
  private isSettingData = false
  private isLoading = false
  private localValue = ''

  private get isEmptyText() {
    return getCountLetters(this.localValue) === 0
  }

  private get enabledSpellCheck() {
    return !!this.config?.extraPlugins?.includes('sirius-spell-check')
  }

  private get localListeners() {
    const listeners = { ...this.$listeners }

    delete listeners.input
    delete listeners.focus
    delete listeners.ready

    return listeners
  }

  private get localConfig() {
    const extraPlugins = this.config?.extraPlugins
      ?.split(',')
      .filter((pluginName) => pluginName.trim() !== 'sirius-spell-check')
      .join(',')

    return this.config && { ...this.config, extraPlugins }
  }

  private get hiddenToolbar() {
    return !this.localConfig?.toolbar?.length
  }

  private onReady(editor: CKEDITOR.editor) {
    const inputListenner = (value: string) => {
      if (this.valueType === 'plain-text') {
        value = cleanEditorText(htmlToText(value))

        if (this.maxlength && getCountLetters(value) > this.maxlength) {
          value = cleanEditorText(
            sliceText({
              text: value,
              start: 0,
              end: this.maxlength,
            })
          )
        }
      }

      this.isLoading = false
      this.localValue = value
      this.$emit('input', value)
    }

    const debouncedInput = this.throttle
      ? debounce(inputListenner, this.throttle, {
          maxWait: this.throttle,
        })
      : null

    const onInput = (value: string) => {
      if (this.isSettingData) return

      if (debouncedInput) {
        this.isLoading = true
        debouncedInput(value)
      } else {
        inputListenner(value)
      }
    }

    const onChangeEditor = (value = editor.getData()) => {
      this.localValue = value
      onInput(value)
      this.$emit('update:value', value)
    }

    const onFocusEditor = (event: CKEDITOR.eventInfo) => {
      this.isFocused = true

      this.$emit('focus', event, editor)
    }

    const onBlurEditor = (event: CKEDITOR.eventInfo) => {
      this.isFocused = false
      this.$emit('blur', event, editor)
    }

    const onKeyEditor = (event: CKEDITOR.eventInfo) => {
      const keyCode = event.data.domEvent.$.key
      const metaKey = event.data.domEvent.$.metaKey
      const ctrlKey = event.data.domEvent.$.ctrlKey

      if (this.valueType === 'plain-text') {
        if (keyCode === 'Enter') {
          event.cancel()

          return
        }

        const [range] = editor.getSelection().getRanges()

        if (
          range.startOffset !== range.endOffset ||
          [
            'Backspace',
            'Delete',
            'Meta',
            'ArrowLeft',
            'ArrowRight',
            'ArrowUp',
            'ArrowDown',
          ].includes(keyCode) ||
          metaKey ||
          ctrlKey
        ) {
          return
        }

        if (
          this.maxlength &&
          getCountLetters(editor.getData()) >= this.maxlength
        ) {
          event.cancel()
        }
      }
    }

    if (this.enabledSpellCheck) {
      new SpellCheck({
        editor,
        onInput: (value) => {
          onChangeEditor(value)
        },
      })
    } else {
      editor.on('change', () => {
        onChangeEditor()
      })
    }

    editor.on('paste', (eventInfo) => {
      if (
        eventInfo.data.method !== 'paste' &&
        !['html', 'text'].includes(eventInfo.data.type)
      ) {
        return
      }

      eventInfo.data.dataValue = decodeHTMLString(eventInfo.data.dataValue)
    })

    editor.on('afterPaste', () => {
      if (this.maxlength) {
        const pastedText =
          this.valueType === 'plain-text'
            ? cleanEditorText(htmlToText(editor.getData()))
            : editor.getData()

        if (getCountLetters(pastedText) > this.maxlength) {
          const slicedText = cleanEditorText(
            sliceText({
              text: pastedText,
              start: 0,
              end: this.maxlength,
            })
          )

          editor.setData(slicedText, {
            callback: () => {
              const range = editor.createRange()

              range.moveToElementEditEnd(range.root)

              editor.getSelection().selectRanges([range])

              editor.fire('unlockSnapshot')
            },
          })

          this.localValue = slicedText
        }
      }
    })

    editor.on('focus', onFocusEditor)
    editor.on('blur', onBlurEditor)
    editor.on('key', onKeyEditor)

    editor.on('setData', () => {
      this.isSettingData = true
    })

    editor.on('afterSetData', () => {
      this.isSettingData = false
    })

    this.$watch(
      () => {
        return this.value
      },
      (value) => {
        this.localValue = value
      },
      {
        immediate: true,
      }
    )

    let loadingTimeoutId = 0

    this.$watch(
      () => {
        return this.isLoading
      },
      (isLoading) => {
        window.clearTimeout(loadingTimeoutId)

        if (isLoading) {
          this.$emit('update:loading', true)
        } else {
          loadingTimeoutId = window.setTimeout(() => {
            this.$emit('update:loading', false)
          }, 100)
        }
      }
    )

    this.$watch(
      () => {
        return [this.isFocused, this.localValue, this.isSettingData].join('-')
      },
      () => {
        if (this.isFocused || this.isSettingData) return

        const oldValue = editor.getData()
        const newValue = this.localValue

        if (oldValue === newValue) return

        editor.fire('lockSnapshot')

        editor.setData(this.localValue, {
          callback: () => {
            editor.fire('unlockSnapshot')
          },
        })
      },
      {
        immediate: true,
      }
    )

    this.$watch(
      () => {
        return this.hiddenToolbar
      },
      (hiddenToolbar) => {
        const toolbarEl = document.getElementById(`cke_${editor.name}`)

        if (!toolbarEl) return

        toolbarEl.style.opacity = hiddenToolbar ? '0' : ''
        toolbarEl.style.pointerEvents = hiddenToolbar ? 'none' : ''

        if (this.toolbarClassName) {
          toolbarEl.classList.add(this.toolbarClassName)
        }
      },
      {
        immediate: true,
      }
    )

    this.$watch(
      () => {
        return [this.isEmptyText, this.placeholder].join('-')
      },
      () => {
        if (typeof this.placeholder !== 'string') return

        if (this.isEmptyText && this.placeholder) {
          editor
            .editable()
            .setAttribute('data-cke-editorplaceholder', this.placeholder)

          editor
            .editable()
            .$.style.setProperty(
              '--cke-editorplaceholder',
              JSON.stringify(this.placeholder)
            )
        } else {
          editor.editable().removeAttribute('data-cke-editorplaceholder')

          editor.editable().$.style.removeProperty('--cke-editorplaceholder')
        }
      },
      {
        immediate: true,
      }
    )

    this.$emit('ready', editor)

    this.$once('hook:beforeDestroy', () => {
      window.clearTimeout(loadingTimeoutId)
      debouncedInput?.cancel()
      this.$emit('update:loading', false)
    })

    this.$once('hook:destroyed', () => {
      // Костыль для очистки памяти, т.к. происходит утечка в ckeditor.js
      Object.keys(editor).forEach((key) => {
        // @ts-ignore
        editor[key] = null
      })
    })
  }

  private validateLength() {
    if (!this.maxlength) {
      return true
    }

    return this.maxlength - getCountLetters(this.localValue) >= 0
  }
}
